import { JobsScrapeStatus } from "@/enum/scrape-enum";

/**
 * The visual weight of a Google Maps source's status chip. The dropdown maps each tone to the
 * existing Tailwind status tokens; nothing here knows about classes.
 */
export type MapSourceStatusTone = "neutral" | "running" | "success" | "error";

export interface MapSourceStatusLabel {
	/** Translation key under `list.sources.maps.status`. */
	key: string;
	tone: MapSourceStatusTone;
}

/**
 * Every stored `JobsScrapeStatus` a Google Maps row can hold (PREC-4994), mapped to the label the
 * Sources menu shows. The classification is total: the API owns the enum, so a value this build
 * does not know about yet still gets a safe label instead of a blank chip.
 */
const STATUS_LABELS: Record<JobsScrapeStatus, MapSourceStatusLabel> = {
	[JobsScrapeStatus.DRAFT]: { key: "list.sources.maps.status.draft", tone: "neutral" },
	[JobsScrapeStatus.TRIGGERED]: { key: "list.sources.maps.status.queued", tone: "neutral" },
	[JobsScrapeStatus.IN_PROGRESS]: { key: "list.sources.maps.status.running", tone: "running" },
	[JobsScrapeStatus.PERSON_FINDING]: {
		key: "list.sources.maps.status.findingPeople",
		tone: "running",
	},
	[JobsScrapeStatus.WAITING]: { key: "list.sources.maps.status.waiting", tone: "neutral" },
	[JobsScrapeStatus.DONE]: { key: "list.sources.maps.status.done", tone: "success" },
	[JobsScrapeStatus.ERROR]: { key: "list.sources.maps.status.error", tone: "error" },
};

/** A value the web enum does not carry yet — never a blank chip. */
const UNKNOWN_LABEL: MapSourceStatusLabel = {
	key: "list.sources.maps.status.unknown",
	tone: "neutral",
};

export const mapSourceStatusLabel = (
	status: JobsScrapeStatus | string | null | undefined
): MapSourceStatusLabel => {
	if (status && Object.hasOwn(STATUS_LABELS, status)) {
		return STATUS_LABELS[status as JobsScrapeStatus];
	}

	return UNKNOWN_LABEL;
};
